import useDynamicWidth from "@/components/DatasetViewerTable/DatasetViewerTableCell/hooks/useDynamicWidth";
import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";
import { DivButton } from "@/components/ui/button";
import { RefreshIcon } from "@heroicons/react/outline";
import { useReportContext } from "../../report-context";
import { CompletedCell } from "./CellTypes/CompletedCell";
import { FailedCell } from "./CellTypes/FailedCell";
import { HumanInputCell } from "./CellTypes/HumanInputCell";
import { StatusCell, StatusCellType } from "./CellTypes/StatusCell";
import { useRetryReportCell } from "./hooks/useRetryReportCell";

const RowCell = ({ rowId, columnId }: { rowId: number; columnId: number }) => {
  const reportContext = useReportContext();
  const cell: ReportCell = reportContext.getCellByRowAndColumn(rowId, columnId);
  const width = useDynamicWidth(cell?.display_value?.value || "");

  return (
    <div className={`${width} mx-auto h-full max-h-[250px] max-w-[40vw]`}>
      <RowCellContent rowId={rowId} columnId={columnId} />
    </div>
  );
};

const RowCellContent = ({
  rowId,
  columnId,
}: {
  rowId: number;
  columnId: number;
}) => {
  const reportContext = useReportContext();
  const cell: ReportCell = reportContext.getCellByRowAndColumn(rowId, columnId);
  const column: ReportColumn = reportContext.getColumnById(columnId);

  const { retryReportCell, isLoading: retryReportCellIsLoading } =
    useRetryReportCell(cell?.id);

  if (!cell) {
    return <StatusCell status={StatusCellType.LOADING} />;
  }

  if (column.column_type === ColumnType.HUMAN) {
    return (
      <HumanInputCell
        key={`${cell?.id}-${column.configuration.data_type}-${column.configuration.ui_element.component}-${cell.value?.value}`}
        cell={cell}
        column={column}
      />
    );
  }

  const status = cell.status.toLowerCase() as StatusCellType;

  switch (status) {
    case "queued":
    case "running":
      return (
        <div className="relative flex h-full items-center justify-center">
          <div className="flex-grow">
            <StatusCell status={status} />
          </div>
          <div className="absolute right-2 top-2 z-10 flex justify-end overflow-visible p-1 opacity-0 transition-opacity duration-200 group-hover/Cell:opacity-100 ">
            <DivButton
              size="tinyIcon"
              variant="outline"
              onClick={() => retryReportCell()}
              isLoading={retryReportCellIsLoading}
            >
              <RefreshIcon className="h-4 w-4 cursor-pointer text-gray-600 hover:text-gray-700" />
            </DivButton>
          </div>
        </div>
      );
    case "completed":
      return (
        <CompletedCell
          cell={cell}
          column={column}
          retryReportCell={retryReportCell}
          retryReportCellIsLoading={retryReportCellIsLoading}
        />
      );
    case "failed":
      return (
        <FailedCell
          cell={cell}
          retryReportCell={retryReportCell}
          retryReportCellIsLoading={retryReportCellIsLoading}
        />
      );
  }

  return null;
};

export default RowCell;
